// =============================================
// SBDPanel — Semantic Boundary Declaration (osobny panel)
// =============================================

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { useBoundaries } from "@/hooks/useBoundaries";
import type { ThematicMap } from "@/types/thematicMaps";

interface SBDPanelProps {
  map: ThematicMap;
  onNext?: () => void;
}

export function SBDPanel({ map, onNext }: SBDPanelProps) {
  const { loading, boundaries, generateBoundaries } = useBoundaries();

  const owned = boundaries?.owned || map.boundaries_owned || [];
  const notOwned = boundaries?.not_owned || map.boundaries_not_owned || [];
  const adjacent = boundaries?.adjacent || map.boundaries_adjacent || [];
  const hasSBD = owned.length > 0;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-2">
          <ShieldCheck className="h-6 w-6 text-primary" />
          Semantic Boundary Declaration (SBD)
        </h2>
        <p className="text-sm text-muted-foreground">
          SBD definiuje granice tematyczne serwisu: co serwis <strong>posiada</strong> autorytatywnie,
          czego <strong>nie pokrywa</strong>, i co tylko <strong>referencjonuje</strong> (mosty).
          Generowane automatycznie z Macro Statement + Scope IN/OUT przez AI.
        </p>
      </div>

      {/* Generate button */}
      <div className="flex items-center gap-3">
        <Button
          onClick={() => generateBoundaries(map.id)}
          disabled={loading || !map.macro_statement?.trim()}
          size="lg"
        >
          <ShieldCheck className="mr-2 h-5 w-5" />
          {loading ? "Generuję SBD..." : hasSBD ? "Odśwież SBD" : "Generuj SBD z AI"}
        </Button>
        {!map.macro_statement?.trim() && (
          <p className="text-sm text-amber-500/90">
            Najpierw wypełnij Macro Statement w kroku Macro.
          </p>
        )}
      </div>

      {/* Results */}
      {hasSBD ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border-2 border-green-500/30 rounded-xl p-5 bg-green-500/10">
            <h3 className="text-sm font-bold text-green-700 dark:text-green-400 mb-3 flex items-center gap-2">
              ✅ Owned — nasze tematy ({owned.length})
            </h3>
            <p className="text-xs text-green-600 dark:text-green-400/80 mb-3">
              Tematy które serwis pokrywa autorytatywnie. Budujemy pełne korytarze treści.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {owned.map((item: string, i: number) => (
                <Badge key={i} className="bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30 text-xs">
                  {item}
                </Badge>
              ))}
            </div>
          </div>

          <div className="border-2 border-red-500/30 rounded-xl p-5 bg-red-500/10">
            <h3 className="text-sm font-bold text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
              🚫 Not Owned — wykluczone ({notOwned.length})
            </h3>
            <p className="text-xs text-red-600 dark:text-red-400/80 mb-3">
              Tematy których NIE pokrywamy. Naruszenie granic = utrata autorytetu.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {notOwned.map((item: string, i: number) => (
                <Badge key={i} className="bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30 text-xs">
                  {item}
                </Badge>
              ))}
            </div>
          </div>

          <div className="border-2 border-amber-500/30 rounded-xl p-5 bg-amber-500/10">
            <h3 className="text-sm font-bold text-amber-700 dark:text-amber-400 mb-3 flex items-center gap-2">
              🔗 Adjacent — mosty ({adjacent.length})
            </h3>
            <p className="text-xs text-amber-600 dark:text-amber-400/80 mb-3">
              Tematy blisko naszych, referencjonujemy ale nie budujemy pełnych korytarzy.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {adjacent.map((item: string, i: number) => (
                <Badge key={i} className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs">
                  {item}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-border rounded-xl p-10 text-center">
          <ShieldCheck className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
          <p className="text-lg font-medium text-muted-foreground">Brak SBD</p>
          <p className="text-sm text-muted-foreground mt-1">
            Kliknij "Generuj SBD z AI" powyżej. AI przeprowadzi 5 testów diagnostycznych
            i wygeneruje granice semantyczne serwisu.
          </p>
        </div>
      )}

      {/* Next step */}
      {hasSBD && onNext && (
        <div className="flex justify-end pt-4">
          <Button onClick={onNext} size="lg">
            Następny krok: Seeds
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
